import { useState } from "react";
import useBasicColumnConfig from "../../main/config/column/default/BasicColumnConfig";
import useBooleanColumnConfig from "../../main/config/column/default/BooleanColumnConfig";
import DataTableConfig from "../../main/config/dataTable/DataTableConfig";
import DataTable, { useDataTable } from "../../main/DataTable";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import ColumnConfig from "../../main/config/column/ColumnConfig";
import { useLanguageContext } from "../../../../context/LanguageContext";
import useCustomEffect from "../../../../hooks/useCustomEffect";
import useDeleteRowActionConfig from "../../main/config/rowAction/default/DeleteRowActionConfig";
import useDeleteHeaderActionConfig from "../../main/config/headerAction/default/DeleteHeaderActionConfig";
import useExportHeaderActionConfig from "../../main/config/headerAction/default/ExportHeaderActionConfig";
import RowActionConfig from "../../main/config/rowAction/RowActionConfig";
import { faQuestion } from "@fortawesome/free-solid-svg-icons";
import useImportHeaderActionConfig from "../../main/config/headerAction/default/ImportHeaderActionConfig";
import { useAuthContext } from "../../../../context/AuthContext";
import QuestionAnswers from "./question_answers/QuestionAnswers";
import useCompanyColumnConfig from "../../main/config/column/default/CompanyColumnConfig";
import { useFrontofficeContext } from "../../../../context/FrontofficeContext";

function VisitorLogsDataTable() {
  const callEvaApi = useEvaApi();
  const frontofficeContext = useFrontofficeContext();
  const { translateEntity, translate } = useLanguageContext();
  const authContext = useAuthContext();
  const [fields, setFields] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [openedQuestionsRow, setOpenedQuestionsRow] = useState(null);

  //Create the datatable config
  const config = new DataTableConfig("visitor_logs");

  //Set the datatable fetch info
  config.setFetchRequest("visitor_logs");
  config.setFetchParams({
    with_visitor_log_company_data: true,
    with_visitor_log_location_data: true,
    with_visitor_log_visitor_type_data: true,
    with_visitor_type_identifier_fields_data: true,
    with_visitor_log_employee_data: true,
    with_visitor_log_sign_in_terminal_data: true,
  });

  //Set default order
  config.setOrderBy("created_at");
  config.setOrderDirection("desc");

  //Set the datatable columns
  config.setColumns([
    useCompanyColumnConfig(),

    useBasicColumnConfig("location.name").setData(
      (row) => row.location?.name ?? row.location_name,
    ),
    useBasicColumnConfig("registration_code"),

    useBasicColumnConfig("identifier_string")
      .setData((row) => {
        return frontofficeContext.getVisitorIdentifierString(row);
      })
      .setSearchable(false)
      .setOrderable(false),

    useBasicColumnConfig("visitor_type.name").setData((row) =>
      row.visitor_type
        ? translateEntity(row.visitor_type, "label")
        : row.visitor_type_name,
    ),
    useBasicColumnConfig("employee.name").setData(
      (row) => row.employee?.name ?? row.employee_name,
    ),

    ...fields.map((field) =>
      new ColumnConfig()
        .setKey(`fields.${field.key}`)
        .setLabel(translateEntity(field, "label"))
        .setData((row) => getFieldColumnData(field, row))
        .setSearchable(field.type !== "boolean")
        .setActive(false),
    ),

    //The reference columns, which combine the values of all references of the visitor
    ...["reference", "client", "status", "type", "adr", "customs"].map((key) =>
      new ColumnConfig()
        .setKey(`references.${key}`)
        .setLabel(
          translate(`eva.main.validation.attributes.references.${key}`),
        )
        .setData((row) => getReferenceColumnData(row, key))
        .setSearchable(key !== "adr" && key !== "customs")
        .setOrderable(false)
        .setActive(false),
    ),

    useBasicColumnConfig("sign_in_terminal.name").setData(
      (row) => row.sign_in_terminal?.name ?? row.sign_in_terminal_name,
    ),
    useBasicColumnConfig("parking_location.name")
      .setData((row) => row.parking_location?.name ?? row.parking_location_name)
      .setActive(false),
    useBasicColumnConfig("license_plate").setActive(false),
    useBasicColumnConfig("signed_in_at"),
    useBasicColumnConfig("signed_out_at"),
    useBooleanColumnConfig("auto_signed_out").setActive(false),
    useBasicColumnConfig("created_at").setUseFromToDateTime(true),
  ]);

  //Set the datatable row actions
  config.setRowActions([
    new RowActionConfig()
      .setIcon(faQuestion)
      .setTitle(translate("eva.main.entities.questions"))
      .setCallback(onQuestionsOpen)
      .setLoadingGroups("visitor_logs_questions_fetch")
      .setHidden(
        (row) =>
          !row.question_answers ||
          (Array.isArray(row.question_answers) &&
            row.question_answers.length === 0),
      ),

    useDeleteRowActionConfig("visitor_log").setTitle(
      translate("eva.main.entities.states.delete", [
        {
          key: "entities",
          value: translate("eva.main.entities.visitor_logs"),
        },
      ]),
    ),
  ]);

  //Set the datatable header actions
  config.setHeaderActions([
    useDeleteHeaderActionConfig("visitor_log"),
    useExportHeaderActionConfig("visitor_logs"),
    useImportHeaderActionConfig("visitor_logs").setHidden(
      authContext.auth?.user?.role_type !== "superadmin",
    ),
  ]);

  //Set the rows to be selectable
  config.setSelectableRows(true);
  config.setShowSavedFilters(true);

  //Create the datatable object
  const dataTable = useDataTable(config);

  /**
   * This effect will fetch the fields from the API
   */
  useCustomEffect(() => {
    fetchFields();
    fetchQuestionnaires();
  });

  /**
   * This method will fetch the fields from the API
   */
  async function fetchFields() {
    await callEvaApi(
      new EvaApiCall("fields")
        .setLoadingGroup(config.getFetchKey())
        .setParams({ with_field_options_data: true })
        .setAlertError(true)
        .setAlertSuccess(false)
        .setOnSuccess((response) => {
          setFields(response.data.data.records);
        }),
    );
  }

  /**
   * This effect will fetch the questionnaires from the API
   */
  async function fetchQuestionnaires() {
    await callEvaApi(
      new EvaApiCall("questionnaires")
        .setLoadingGroup(config.getFetchKey())
        .setParams({
          with_questionnaire_questions_data: true,
          with_question_answers_data: true,
        })
        .setOnSuccess((response) => {
          setQuestionnaires(response.data.data.records);
        }),
    );
  }

  /**
   * This method will return the column data for the given field and row
   * @param {object} field
   * @param {object} row
   * @returns {any}
   */
  function getFieldColumnData(field, row) {
    // Find the field data for the given field
    const fieldValue = row.fields?.[field.key];
    if (!fieldValue) {
      return "";
    }

    // If the field is a select, return the translation of the selected option
    if (field.type === "select") {
      const option = field.options.find((o) => o.key === fieldValue);
      return option ? translateEntity(option, "label") : "";
    }
    // If the field is a boolean, return the translation of the value
    else if (field.type === "boolean") {
      return fieldValue === "1"
        ? translate("eva.main.general.yes")
        : translate("eva.main.general.no");
    }

    // Otherwise, return the value of the field
    return fieldValue ?? "";
  }

   /**
   * This method will return the column data for the given reference key and row
   * @param {object} row
   * @param {string} key
   * @returns {string}
   */
  function getReferenceColumnData(row, key) {
    return (row.references ?? [])
      .map((reference) => {
        if (key === "adr" || key === "customs") {
          return reference[key]
            ? translate("eva.main.general.yes")
            : translate("eva.main.general.no");
        }
        return reference[key] ?? "";
      })
      .filter((value) => value !== "")
      .join(", ");
  }

  /**
   * This method will open the questions for the given row
   * @param {object} row
   */
  async function onQuestionsOpen(row) {
    const questionAnswers = [];

    Object.keys(row.question_answers ?? {}).forEach((questionnaireName) => {
      const questionnaire = questionnaires.find(
        (questionnaire) => questionnaire.name === questionnaireName,
      );
      if (!questionnaire) return;

      Object.keys(row.question_answers[questionnaireName] ?? {}).forEach(
        (questionName) => {
          const answerName =
            row.question_answers[questionnaireName][questionName];
          const question = questionnaire.questions.find(
            (question) => question.name === questionName,
          );
          const answer = question?.answers.find(
            (answer) => answer.name === answerName,
          );
          if (answer && question) {
            questionAnswers.push({
              question,
              answer,
            });
          }
        },
      );
    });

    setOpenedQuestionsRow({
      ...row,
      question_answers: questionAnswers,
    });
  }

  return (
    <>
      <DataTable controller={dataTable} />

      {openedQuestionsRow && (
        <QuestionAnswers
          questionAnswers={openedQuestionsRow.question_answers}
          onClose={() => setOpenedQuestionsRow(null)}
        />
      )}
    </>
  );
}

export default VisitorLogsDataTable;
